import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../redux/slices/authSlice";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../rbac/Can";
import { updateAbility } from "../rbac/Ability";
import { homePath } from "../rbac/homePath";
import Logo from "../components/Logo";

export default function Login() {
  const ability = useAbility(AbilityContext);
  const { user, token, error } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    updateAbility(ability, user);

    if (token) navigate(homePath(ability), { replace: true });
  }, [token, navigate, ability, user]);

  useEffect(() => {
    if (error) {
      if (error.message) {
        toast.error(error.message);
      } else if (error.errors) {
        for (const messages of Object.values(error.errors)) {
          messages.forEach((message) => toast.error(`${message}`));
        }
      }
    }
  }, [error]);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email format").required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const handleLogin = (values) => {
    dispatch(loginUser(values));
  };

  return (
    <div className="login-shell">
      <div className="login-panel">
        <div className="login-card">
          <div className="mb-8 flex justify-center">
            <Logo variant="dark" to={null} />
          </div>
          <h4 className="font-display text-3xl font-semibold text-slate-900">Welcome back</h4>
          <p className="mt-2 text-sm text-slate-500">
            Sign in to manage accounts, figures, and operations.
          </p>

          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            {({ isSubmitting }) => (
              <Form className="mt-8 space-y-4">
                <div>
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <Field
                    id="email"
                    name="email"
                    placeholder="you@company.com"
                    type="email"
                    className="form-control"
                  />
                  <ErrorMessage name="email" component="div" className="form-error" />
                </div>

                <div>
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <Field
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    type="password"
                    className="form-control"
                  />
                  <ErrorMessage name="password" component="div" className="form-error" />
                </div>

                <button type="submit" className="btn-primary w-full justify-center" disabled={isSubmitting}>
                  {isSubmitting ? "Signing in..." : "Sign in"}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <div className="login-brand-panel">
        <div className="relative z-10 max-w-lg px-10 text-center">
          <Logo variant="light" to={null} />
          <p className="mt-8 font-display text-4xl leading-tight text-white">
            Precision tools for modern wagering operations.
          </p>
          <p className="mt-4 text-sm leading-7 text-slate-400">
            Monitor accounts, track figures, and manage your betting network from one refined control center.
          </p>
        </div>
      </div>
    </div>
  );
}
